import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDietary } from "@/context/DietaryContext";
import { useDietaryProfile } from "@/context/DietaryProfileContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LogOut, Save, Users } from "lucide-react";
import { toast } from "sonner";
import { formatKcal } from "@/lib/dietary";
import LoyaltyWalletCard from "@/components/LoyaltyWalletCard";
import PasskeyManager from "@/components/auth/PasskeyManager";
import StandardOrdersSection from "@/components/StandardOrdersSection";
import ProfilePhotoCapture from "@/components/ProfilePhotoCapture";
import AccountContextSwitcher from "@/components/AccountContextSwitcher";
import BusinessSettingsHub from "@/components/business/BusinessSettingsHub";
import { useMeState } from "@/hooks/useMeState";

export default function AccountPage() {
  const { user, checking, logout } = useAuth();
  const { legend: DIETARY } = useDietary();
  const { legendAllergens, legendAilments,
            update: updateProfileCtx } = useDietaryProfile();
  const navigate = useNavigate();
  const { state: meState, setActive: setContextActive } = useMeState();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (checking) return;
    if (!user) {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        // /me/profile is customer-only (business_admins hit 403 —
        // that's fine, they still get to see the account shell with
        // the vendor↔member switcher + business context).
        const [pRes, oRes] = await Promise.allSettled([
          api.get("/me/profile"),
          api.get("/me/orders"),
        ]);
        if (pRes.status === "fulfilled") {
          setProfile(pRes.value.data);
        } else {
          const status = pRes.reason?.response?.status;
          if (status === 401 || status === 403 || status === 404) {
            // Non-customer (vendor/admin) — fall back to auth ctx.
            setProfile({
              email:         user.email,
              name:          user.name || "",
              phone:         "",
              dietary_tags:  [],
              max_calories:  null,
              allergens:     [],
              ailments:      [],
              dietary_notes: "",
              _vendor_only:  true,
            });
          } else {
            toast.error(
              formatApiError(pRes.reason?.response?.data?.detail)
                || pRes.reason?.message);
          }
        }
        if (oRes.status === "fulfilled") {
          setOrders(oRes.value.data || []);
        } else {
          // Silently accept "no orders" for non-customers.
          setOrders([]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user, checking, navigate]);

  function toggleTag(code) {
    setProfile((p) => {
      const next = new Set(p.dietary_tags || []);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...p, dietary_tags: Array.from(next) };
    });
  }

  function toggleAllergen(code) {
    setProfile((p) => {
      const next = new Set(p.allergens || []);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...p, allergens: Array.from(next) };
    });
  }

  function toggleAilment(code) {
    setProfile((p) => {
      const next = new Set(p.ailments || []);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...p, ailments: Array.from(next) };
    });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        phone: profile.phone,
        dietary_tags: profile.dietary_tags,
        max_calories: profile.max_calories ? Number(profile.max_calories) : null,
        allergens:     profile.allergens     || [],
        ailments:      profile.ailments      || [],
        dietary_notes: profile.dietary_notes || "",
      };
      const { data } = await api.patch("/me/profile", payload);
      setProfile(data);
      // Sync the global context so the menu/recipe pages pick up
      // the new allergen card immediately without a page reload.
      await updateProfileCtx({
        allergens:     data.allergens     || [],
        dietary_tags:  data.dietary_tags  || [],
        ailments:      data.ailments      || [],
        dietary_notes: data.dietary_notes || "",
      });
      toast.success("Profile saved · menu will now hide your allergens");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function onLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  async function savePhoto({ dataUrl }) {
    // Reuse the meetup profile photo endpoint (base64, server-side
    // decode + JPEG persist to /api/uploads/meetup/profile/…).
    // File uploads are NOT accepted — only live camera captures.
    const res = await api.post("/hustlemeet/profile/photo", { image_b64: dataUrl });
    const photo_url = res?.data?.photo_url;
    if (photo_url) {
      setProfile((p) => ({ ...p, avatar_url: photo_url, photo_url }));
    }
    return res;
  }

  if (checking || loading) {
    return (
      <div
        data-testid="account-loading"
        className="min-h-[60vh] flex items-center justify-center text-[#5C625E]"
      >
        Loading…
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div data-testid="account-page" className="bg-[#FDFBF7] text-[#3C423F]">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
            My Account
          </p>
          {meState?.has_vendor_profile && meState?.has_member_profile && (
            <AccountContextSwitcher
              state={meState}
              onChange={setContextActive}
            />
          )}
        </div>
        <div className="flex items-baseline justify-between flex-wrap gap-3 mt-3">
          <h1 className="font-serif-display text-4xl sm:text-5xl">
            {meState?.active_context === "business" ? "Business · " : ""}
            Buongiorno{profile.name ? `, ${profile.name.split(" ")[0]}` : ""}
          </h1>
          <button
            type="button"
            data-testid="account-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-1 text-sm text-[#C05746] hover:text-[#A64A3C] font-medium"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
        <p className="mt-2 text-[#3C423F]">
          Signed in as <code>{profile.email}</code>.
          {meState?.active_context === "business" && meState?.business_id && (
            <span className="ml-2 text-xs text-[#5C625E]">
              (business <code>{meState.business_id}</code>)
            </span>
          )}
        </p>
      </section>

      {/* Profile photo — live camera capture ONLY (no file uploads). */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div
          data-testid="account-photo-card"
          className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8"
        >
          <ProfilePhotoCapture
            currentUrl={profile.avatar_url || profile.photo_url}
            label={profile.name || profile.email}
            onSave={savePhoto}
          />
        </div>
      </section>

      {/* iter268b: Business context takes over the mid-page real
       *  estate — dietary form disappears, vendor stats surface. */}
      {meState?.active_context === "business" && (
        <section
          data-testid="account-business-section"
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8"
        >
          <BusinessSettingsHub
            meState={meState}
            onLogout={onLogout}
            onSwitchPersonal={() => setContextActive("personal")}
          />
        </section>
      )}

      {/* Loyalty wallet stays visible in BOTH contexts. */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <LoyaltyWalletCard />
      </section>

      {/* Household · Family Groups — deep-links to /family where the
       *  diner can master a group, invite kin, upgrade to Lifetime+
       *  and respond to "pay by other patron" requests. */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <Link
          to="/family"
          data-testid="account-family-groups-link"
          className="group flex items-center gap-4 bg-white border border-[#E8E5DF] rounded-2xl p-5 md:p-6 hover:border-[#C05746] hover:shadow-sm transition"
        >
          <div className="rounded-full bg-[#FDEEE9] p-3 shrink-0">
            <Users className="h-5 w-5 text-[#C05746]" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif-display text-2xl text-[#121614]">
              Family Groups
            </h2>
            <p className="text-sm text-[#5C625E] mt-0.5">
              One household across every HustleVerse module —
              split bills with Mum, share menus and allergen locks,
              or unlock Lifetime+ ($25 → $20 back in your wallet).
            </p>
          </div>
          <span className="text-sm font-medium text-[#C05746] group-hover:translate-x-0.5 transition">
            Open →
          </span>
        </Link>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <PasskeyManager />
      </section>

      {/* Personal-context sections (dietary + orders + usuals) —
       *  hidden while the vendor is in business mode. */}
      {meState?.active_context !== "business" && (
      <>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid lg:grid-cols-2 gap-6">
        {/* Profile + dietary preferences */}
        <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8 space-y-5">
          <h2 className="font-serif-display text-2xl">Dietary preferences</h2>
          <p className="text-sm text-[#3C423F]">
            We use these to auto-filter the menu when you're signed in. Anything you set here
            overrides the saved-on-device filter.
          </p>

          <div>
            <Label>Name</Label>
            <Input
              data-testid="account-name-input"
              value={profile.name || ""}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="mt-2 rounded-xl"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              data-testid="account-phone-input"
              value={profile.phone || ""}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              className="mt-2 rounded-xl"
            />
          </div>

          <div>
            <Label>My dietary tags</Label>
            <div
              className="mt-2 flex flex-wrap gap-2"
              data-testid="account-dietary-chips"
            >
              {DIETARY.map((d) => {
                const checked = (profile.dietary_tags || []).includes(d.code);
                return (
                  <button
                    type="button"
                    key={d.code}
                    data-testid={`account-diet-${d.code}`}
                    onClick={() => toggleTag(d.code)}
                    className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                      checked
                        ? "bg-[#5A7247] text-white border-[#5A7247]"
                        : "bg-white border-[#E8E5DF] text-[#5C625E] hover:border-[#5A7247]"
                    }`}
                  >
                    {d.short} · {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Allergen Card ─────────────────────────────────────── */}
          <div>
            <Label>⚠ Allergens to avoid</Label>
            <p className="text-[11px] text-[#5C625E] mt-1">
              Any menu item or recipe containing these will be greyed-out
              and blocked from your order. The kitchen also sees this
              when you book.
            </p>
            <div className="mt-2 flex flex-wrap gap-2"
                    data-testid="account-allergen-chips">
              {legendAllergens.map((a) => {
                const checked = (profile.allergens || []).includes(a.code);
                return (
                  <button type="button"
                          key={a.code}
                          data-testid={`account-allergen-${a.code}`}
                          onClick={() => toggleAllergen(a.code)}
                          className={`text-xs rounded-full border px-3 py-1.5
                                  transition-colors ${
                                    checked
                                      ? "text-white border-rose-700"
                                      : "bg-white border-[#E8E5DF] text-[#5C625E] "
                                        + "hover:border-rose-700"
                                  }`}
                          style={checked
                            ? { backgroundColor: a.color || "#B91C1C" }
                            : {}}>
                    {checked ? "⚠ " : ""}{a.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Ailments / medical card ──────────────────────────── */}
          <div>
            <Label>Medical / dietary conditions</Label>
            <p className="text-[11px] text-[#5C625E] mt-1">
              Shared with the kitchen alongside any booking you make so
              chefs know to adapt seasoning, oils and portions.
            </p>
            <div className="mt-2 flex flex-wrap gap-2"
                    data-testid="account-ailment-chips">
              {legendAilments.map((a) => {
                const checked = (profile.ailments || []).includes(a.code);
                return (
                  <button type="button"
                          key={a.code}
                          data-testid={`account-ailment-${a.code}`}
                          onClick={() => toggleAilment(a.code)}
                          className={`text-xs rounded-full border px-3 py-1.5
                                  transition-colors ${
                                    checked
                                      ? "bg-[#6D28D9] text-white border-[#6D28D9]"
                                      : "bg-white border-[#E8E5DF] text-[#5C625E] "
                                        + "hover:border-[#6D28D9]"
                                  }`}>
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Notes for kitchen (optional)</Label>
            <Textarea
              data-testid="account-dietary-notes"
              value={profile.dietary_notes || ""}
              onChange={(e) => setProfile({
                ...profile, dietary_notes: e.target.value })}
              placeholder="e.g. Severe peanut allergy — EpiPen carrier."
              rows={3}
              className="mt-2 rounded-xl" />
          </div>

          <div>
            <Label>Max calories per dish</Label>
            <Input
              data-testid="account-max-calories"
              type="number"
              min="0"
              step="50"
              value={profile.max_calories || ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  max_calories: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
              placeholder="No limit"
              className="mt-2 rounded-xl"
            />
            <p className="text-[11px] text-[#5C625E] mt-1">Leave blank for no limit.</p>
          </div>

          <Button
            type="button"
            data-testid="account-save"
            onClick={save}
            disabled={saving}
            className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11 w-full"
          >
            <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>

        {/* Order history */}
        <div className="bg-white border border-[#E8E5DF] rounded-2xl p-6 md:p-8">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="font-serif-display text-2xl">Order history</h2>
            <Link
              to="/menu"
              data-testid="account-order-cta"
              className="text-sm text-[#C05746] hover:text-[#A64A3C] font-medium"
            >
              Order again →
            </Link>
          </div>

          {orders.length === 0 ? (
            <div
              data-testid="account-orders-empty"
              className="text-center py-10 text-[#5C625E]"
            >
              <p className="font-serif-display text-2xl text-[#121614]">
                No orders yet
              </p>
              <p className="mt-1 text-sm">
                Pre-orders you place while signed in will appear here.
              </p>
            </div>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="account-orders-list">
              {orders.map((o) => {
                const cals = (o.items || []).reduce(
                  (s, l) => s + (l.calories || 0),
                  0,
                );
                return (
                  <li
                    key={o.id}
                    data-testid={`account-order-${o.id}`}
                    className="border border-[#E8E5DF] rounded-xl p-4"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-medium">
                        #{o.id.slice(0, 8)} · {o.pickup_date} · {o.pickup_time}
                      </p>
                      <p className="text-sm font-medium">{money(o.total)}</p>
                    </div>
                    <ul className="mt-2 text-xs text-[#5C625E] space-y-0.5">
                      {(o.items || []).slice(0, 4).map((line, i) => (
                        <li key={i}>
                          {line.quantity}× {line.name}
                        </li>
                      ))}
                      {(o.items || []).length > 4 && (
                        <li className="italic">
                          + {(o.items || []).length - 4} more
                        </li>
                      )}
                    </ul>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-[#5C625E]">
                      <span>
                        Status:{" "}
                        <span className="font-medium text-[#121614]">{o.status}</span>
                      </span>
                      {cals > 0 && <span>≈ {formatKcal(cals)}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Saved standard orders — "Order again" across all venues */}
      <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6 mt-4">
        <h2 className="text-xl font-medium" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          My usual orders
        </h2>
        <p className="text-sm text-[#5C625E] mt-1 mb-4">
          One-tap re-order across every venue you frequent — coffee shops, restaurants, day spas, hairdressers.
        </p>
        <StandardOrdersSection userId={user?.id} />
      </section>
      </>
      )}
    </div>
  );
}
